import { createContext, useContext, useRef } from "react";

import type { __internal__ } from "@foxitsoftware/foxit-pdf-sdk-for-web-library/lib";
import type { StoreApi, UseBoundStore } from "zustand";

import { create, useStore } from "zustand";

import type { PDFControllerSlice } from "./state";

import { usePDFControllerSlice } from "./state";

interface PDFStoreApi extends StoreApi<PDFControllerSlice> {
  (callback: (state: PDFControllerSlice) => unknown): unknown;
}

const StoreContext = createContext<PDFStoreApi | null>(null);

interface StoreProviderProps {
  children?: React.ReactNode;
}

/**
 *
 * StoreProvider is a wrapper around Zustand's store. It stores PDFControllerSlice - state management for PDFViewer controls, such as zoom, page navigation, etc.
 *
 */

export const PDFViewerStoreProvider = ({ children }: StoreProviderProps) => {
  const storeRef =
    useRef<UseBoundStore<StoreApi<PDFControllerSlice>>>(undefined);

  const createPDFControllerSlice = usePDFControllerSlice();

  if (!storeRef.current) {
    /**
     * Combining slices into one store for modularity and easier state management.
     * https://github.com/pmndrs/zustand/blob/main/docs/guides/typescript.md#slices-pattern
     */

    const store = create<PDFControllerSlice>()(createPDFControllerSlice);

    storeRef.current = store;
  }

  return <StoreContext value={storeRef.current}>{children}</StoreContext>;
};

export const usePDFStore = <R,>(selector: (value: PDFControllerSlice) => R) => {
  const store = useContext(StoreContext);
  if (!store) {
    console.error("PDF Viewer store is not initialized yet");
    throw new Error("PDF Viewer store is not initialized yet");
  }

  const data = useStore(store, selector);

  if (typeof window === "undefined") {
    return { data: null, isServer: true as const };
  }

  return { data, isServer: false as const };
};
